import { Injectable } from "@angular/core";
import { HTTPService } from "../../shared/services/http.service";
import { ManagedClient } from "../../shared/models/managed-client";
import { PaginatedResultResponse } from "../../shared/models/paginated-results-response";
import { User } from "app/shared/models/user";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { Observable } from "rxjs/Observable";
import { AssignmentHydratorService } from "./assignment-hydrator.service";

@Injectable()
export class ClientService {

	private _dac$http: HTTPService;
	private _clientNotice: BehaviorSubject<ManagedClient>;

	constructor(dac$http: HTTPService,
		private assignmentHydrator:AssignmentHydratorService) {
		this._dac$http = dac$http;
		this._clientNotice = new BehaviorSubject<ManagedClient>(null);
	}

	generateClientId() : Promise<string> {
		return this._dac$http.post("/utils/generateClientId", {}).then((serverResponse) => {
			let data:{id:string} = serverResponse.json();
			return data.id;
		});
	}

	getClientObservable(): Observable<ManagedClient> {
		return this._clientNotice;
	}

	getClientList(user?: User, cursor?: string): Promise<PaginatedResultResponse<ManagedClient>> {
		var data: any = {
			limit: 10
		};
		if (cursor) {
			data.cursor = cursor;
		}
		if (user) {
			data.user = user.id;
		}
		return this._dac$http.get("/reports/client.json", data)
			.then((serverResponse) => {
				let data = serverResponse.json() as PaginatedResultResponse<ManagedClient>
				data.results = data.results.map((record) => this.hydrateClient(record));
				return data;
			});
	}

	sendClientEmail(clientId:string, subj:string, message:string, isTest:boolean) {
		var data = {
			subject:subj
			,message:message
			,isTest: (isTest ? 1 : 0) 
		};
		return this._dac$http.post("/clients/" + clientId + "/messages/", data)
		.then((serverResponse) => {
			return {};
		});
	}

	createClient(client: ManagedClient) {
		return this._dac$http.post("/clients/", client)
			.then((serverResponse) => {
				let newClient = this.hydrateClient(client);
				let json = serverResponse.json(); // we have the server unique id here
				// TODO: stephen change the server api to return the same id we use for the other clients.
				this._clientNotice.next(newClient);
				return newClient;
			})
			.catch((response) => {
				console.error(response);
				if (response.status === 400) {
					let data = response.json() as any;
					if (data) {
						throw data;
					}
				}
				else {
					throw response;
				}
			});
	}

	/**
	 * Any in memory modifications of the client, or client properties need to be notified
	 * to other services and this method handles that.
	 * @param client 
	 */
	syncLocalClient(client:ManagedClient) {
		this._clientNotice.next(client);
	}

	saveClient(client: ManagedClient) {
		return this._dac$http.post("/clients/" + client.id, client)
			.then((serverResponse) => {
				let data = serverResponse.json() as any;
				this._clientNotice.next(client);
				return data;
			});
	}

	getClient(clientId: string): Promise<ManagedClient> {
		return this._dac$http.get("/clients/" + clientId)
			.then((serverResponse) => {
				return this.hydrateClient(serverResponse.json());
			});
	}

	searchClientList(searchQueryObj: {firstName?:string, lastName?:string, id?:string}, user?: User, cursor?: string) {
		let search:any = searchQueryObj || {};
		search.limit = 10;
		if (cursor) {
			search.cursor = cursor;
		}
		if (user) {
			search.user = user.id;
		}
		return this._dac$http.get("/reports/client.json", search)
			.then((serverResponse) => {
				let data = serverResponse.json() as PaginatedResultResponse<ManagedClient>
				data.results = data.results.map((record) => this.hydrateClient(record));
				return data;
			});
	}

	private hydrateClient(record): ManagedClient {
		let user = new User();
		let client = new ManagedClient();
		Object.assign(client, record);
		if (record.assignedUser) {
			client.user = Object.assign(user, record.assignedUser);
		}
		if (client.assignments) {
			client.assignments = record.assignments.map(a => this.assignmentHydrator.hydrateAssignment(a));
		}
		return client;
	}
}


// WEBPACK FOOTER //
// ./src/app/shared/services/client.service.ts